import { inject, Injectable } from '@angular/core';
import { IApiErrorResponse, IApiPutResponse, IApiStandardResponse } from '@core/models/api/api.models';
import { OtpChannel } from '@core/models/custom-types.model';
import { IUser, User } from '@core/models/user.model';
import { ApiService } from '@core/services/api/api.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Console } from './logger.service';
import { AppStore } from './store/app.store';
import { UserRepository } from './user.repository.service';
import { VerificaService } from './verifica.service';


/**
 * Interfaccia per la richiesta di aggiornamento password di un utente autenticato.
 */
export interface IUserPasswordUpdateRequest {
  password: string;
  password_confirmation: string;
}

/**
 * Interfaccia per l'aggiornamento dei consensi privacy dell'utente autenticato
 * (`PUT user/consensi-privacy`). Tutti i campi sono obbligatori lato API.
 */
export interface IUserConsensiPrivacyRequest {
  privacy_trattamento_dati: boolean;
  privacy_dati_sensibili: boolean;
  privacy_attivita_commerciali: boolean;
  privacy_attivita_conto_terzi: boolean;
}

/**
 * Interfaccia per la richiesta di aggiornamento del codice fiscale
 * dell'utente autenticato.
 */
export interface IUserCodiceFiscaleRequest {
  codice_fiscale: string;
  nome: string;
  cognome: string;
}

/**
 * Servizio per la gestione delle operazioni relative all'utente.
 *
 * Responsabilità principali:
 * - Recupero e aggiornamento dati utente tramite API
 * - Salvataggio dati utente nello store
 *
 * Dipendenze:
 * - ApiService: per le chiamate HTTP alle API
 * - UserRepository: per la persistenza locale dei dati utente
 * - AppStore: per la gestione dello stato globale
 * - AuthService: per la gestione dell'autenticazione
 *
 * @class UserService
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  /** Etichetta per i log del servizio */
  private readonly log_label = 'UserService';

  // Servizi iniettati
  private readonly apiService = inject(ApiService);
  private readonly userRepository = inject(UserRepository);
  private readonly appStore = inject(AppStore);
  private readonly authService = inject(AuthService);
  private readonly verificaService = inject(VerificaService);

  /**
   * usato dall'app che si autentica, qui si usa AuthService.me() per recuperare i dati dell'utente
   *
   *
   * Carica i dati dell'utente corrente dal server e aggiorna il repository locale.
   * Verifica i diritti di accesso dell'utente e, in caso di diritti insufficienti,
   * esegue automaticamente il logout. Se l'utente ha i diritti corretti, recupera
   * i dati e li salva nel repository.
   *
   * **Operazioni eseguite:**
   * 1. Recupera i dati utente dal server tramite API `/user`
   * 2. Verifica che il campo `roles` sia uguale a "user"
   * 3. Se i diritti non sono sufficienti, esegue logout automatico e termina con errore
   * 4. Se i diritti sono corretti, crea un oggetto User con i dati recuperati
   * 5. Salva tutto nel repository locale
   * 6. Aggiorna lo store per notificare i componenti
   *
   * **Controllo sicurezza:**
   * - Verifica che `response.data.roles === 'user'`
   * - In caso di fallimento, esegue `authService.logout()` e reindirizza a `/login`
   *
   * @returns Observable con i dati dell'utente aggiornati
   * @throws {Error} " per accedere all'applicazione" se roles !== "user"
   * @throws {IApiErrorResponse} Errore durante il caricamento dei dati dal server
   */
  getApiUser(): Observable<IUser> {
    const log_pref = this.log_label + '.getApiUser() ';

    return this.apiService.get<IUser>('user').pipe(
      switchMap((userData) => {
        Console.log(log_pref, 'utente caricato, verifica diritti utente', 'info');

        // Verifica dei diritti utente
//        if (userData.roles !== 'user' && userData.roles !== 'admin') {
          if (userData.roles !== 'user' ) {
          Console.log(log_pref, `. Roles ricevuto: ${userData.roles}, richiesto: user`, 'err');

          // Esegui logout e termina il flusso
          this.authService.logout();
          return throwError(() => new Error('Diritti insufficienti per accedere all\'applicazione: ' + userData.roles));
        }

        Console.log(log_pref, 'diritti verificati, salvataggio dati utente', 'info');

        // Crea un nuovo oggetto User con i dati ricevuti
        const userObj = new User(userData);

        // Salva nel repository e aggiorna lo store
        this.userRepository.saveUser(userObj);

        return of(userData);
      }),
      catchError((error) => {
        Console.log(log_pref, 'errore nel caricamento utente: ' + error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Recupera l'utente corrente dal repository (metodo sincrono).
   * @returns User - L'utente corrente
   */
  getCurrentUser(): User {
    return this.userRepository.getUser();
  }

  /**
   * Aggiorna l'utente corrente nel repository e nello store.
   * @param user - L'utente da aggiornare
   */
  updateUser(user: User): void {
    this.userRepository.updateCurrentUser(user);
  }

  /**
   * Compatibilità retroattiva: inoltra la richiesta al servizio verifica contatti.
   */
  richiediCodiceConfermaContatto(type: OtpChannel): Observable<IApiStandardResponse<unknown>> {
    return this.verificaService.richiediCodiceConfermaContatto(type);
  }

  /**
   * Compatibilità retroattiva: inoltra la conferma OTP al servizio verifica contatti.
   */
  confermaContatto(codice_numerico: number): Observable<IApiStandardResponse<unknown>> {
    return this.verificaService.confermaContatto(codice_numerico);
  }

  /**
   * Aggiorna email o cellulare dell'utente corrente (allineato a Premi setApiContatto).
   */
  setApiContatto(
    type: OtpChannel,
    payload:
      | { email: string; email_confirmation: string }
      | { cellulare: string; cellulare_confirmation: string }
  ): Observable<IApiPutResponse> {
    return this.apiService.put<typeof payload>(
      `user/contatto/aggiorna/${type}`,
      payload
    );
  }

  /**
   * Aggiorna la password di un utente.
   *
   * @param payload - Nuova password e conferma password
   * @returns Observable contenente la risposta (tipicamente un messaggio di successo)
   */
  updateUserPassword(
    payload: IUserPasswordUpdateRequest
  ): Observable<string> {
    const log_pref = this.log_label + '.updateUserPassword() ';
    return this.apiService.post<string, IUserPasswordUpdateRequest>(
      `password/modifica`,
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage = log_pref + (error?.message || `Errore nell'aggiornamento della password.`);
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna i consensi privacy dell'utente autenticato.
   *
   * @param payload - Consensi privacy (tutti i campi obbligatori)
   * @returns Observable con la risposta standard (message)
   */
  updateConsensiPrivacy(
    payload: IUserConsensiPrivacyRequest
  ): Observable<IApiPutResponse> {
    const log_pref = this.log_label + '.updateConsensiPrivacy() ';
    return this.apiService.put<IUserConsensiPrivacyRequest>(
      'user/consensi-privacy',
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage =
          log_pref + (error?.message || 'Errore nell\'aggiornamento dei consensi privacy.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }

  /**
   * Aggiorna il codice fiscale e i dati anagrafici dell'utente autenticato.
   *
   * @param payload - Codice fiscale, nome e cognome
   * @returns Observable con la risposta standard (message)
   */
  setCodiceFiscale(
    payload: IUserCodiceFiscaleRequest
  ): Observable<IApiPutResponse> {
    const log_pref = this.log_label + '.setCodiceFiscale() ';
    return this.apiService.put<IUserCodiceFiscaleRequest>(
      'user/codice-fiscale',
      payload
    ).pipe(
      catchError((error: IApiErrorResponse) => {
        const errorMessage =
          log_pref + (error?.message || 'Errore durante la modifica del codice fiscale.');
        Console.log(errorMessage, error, 'err');
        return throwError(() => error);
      })
    );
  }
}
